'use client';

import { LocationMapCard } from '@/components/blocks/LocationMapCard';
import { ShPinIcon } from '@/components/ui/icons';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
  ALL_REGIONS,
  useLocationsView,
  useSelectedRegion,
} from '@/lib/locations/store';
import { cn } from '@/lib/ui/cn';
import type { BlockLocationsMapProps } from '@/types/blocks';
import type { ComponentClinicProps } from '@/types/components';
import { ErrorBoundary } from '@rollbar/react';
import {
  AdvancedMarker,
  APIProvider,
  Map,
  useMap,
} from '@vis.gl/react-google-maps';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useState } from 'react';

type Located = ComponentClinicProps & { latitude: number; longitude: number };

const DEFAULT_ZOOM = 11;
const SINGLE_CLINIC_ZOOM = 15;
const BOUNDS_PADDING = 64;

const mapClass =
  'border-sh-gray h-140 w-full overflow-hidden rounded-3xl border lg:aspect-square lg:h-auto';

const isLocated = (clinic: ComponentClinicProps): clinic is Located =>
  clinic.latitude != null && clinic.longitude != null;

const MapUnavailable = () => {
  const t = useTranslations('LocationsMap');

  return (
    <p className='border-sh-gray text-sh-dark-gray flex h-140 w-full items-center justify-center rounded-3xl border px-6 text-center font-sans text-base leading-[130%] lg:aspect-square lg:h-auto'>
      {t('unavailable')}
    </p>
  );
};

const FitBounds = ({ clinics }: { clinics: Located[] }) => {
  const map = useMap();
  const view = useLocationsView();

  useEffect(() => {
    if (!map || clinics.length === 0) return;

    if (clinics.length === 1) {
      map.setCenter({ lat: clinics[0].latitude, lng: clinics[0].longitude });
      map.setZoom(SINGLE_CLINIC_ZOOM);
      return;
    }

    const lats = clinics.map(c => c.latitude);
    const lngs = clinics.map(c => c.longitude);
    map.fitBounds(
      {
        north: Math.max(...lats),
        south: Math.min(...lats),
        east: Math.max(...lngs),
        west: Math.min(...lngs),
      },
      BOUNDS_PADDING
    );
  }, [map, clinics, view]);

  return null;
};

export const LocationsMap = ({
  regions,
  className,
  ...rest
}: BlockLocationsMapProps) => {
  const apiKey = process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY;
  const mapId = process.env.NEXT_PUBLIC_GOOGLE_MAPS_MAP_ID;

  const view = useLocationsView();
  const region = useSelectedRegion();
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const [openId, setOpenId] = useState<string | null>(null);
  const [lastRegion, setLastRegion] = useState(region);
  const [mapShown, setMapShown] = useState(false);

  const clinics = useMemo(
    () =>
      regions
        .filter(r => region === ALL_REGIONS || r.slug === region)
        .flatMap(r => r.clinics ?? [])
        .filter(isLocated),
    [regions, region]
  );

  if (region !== lastRegion) {
    setLastRegion(region);
    setOpenId(null);
  }

  // Google bills every map load, so we load it only once the user can see it.
  if (!mapShown && (isDesktop || view === 'map')) setMapShown(true);

  useEffect(() => {
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpenId(null);
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, []);

  if (!apiKey || !mapId || clinics.length === 0) {
    return (
      <section
        data-component='locations-map'
        data-testid='locations-map-unavailable'
        className={cn(
          'w-full',
          view === 'map' ? 'block' : 'hidden lg:block',
          className
        )}
        {...rest}
      >
        <MapUnavailable />
      </section>
    );
  }

  const open = clinics.find(clinic => clinic.id === openId);

  return (
    <section
      data-component='locations-map'
      data-testid='locations-map'
      className={cn(
        'relative w-full',
        view === 'map' ? 'block' : 'hidden lg:block',
        className
      )}
      {...rest}
    >
      {mapShown ? (
        <ErrorBoundary fallbackUI={MapUnavailable}>
          <APIProvider apiKey={apiKey}>
            <Map
              mapId={mapId}
              defaultCenter={{
                lat: clinics[0].latitude,
                lng: clinics[0].longitude,
              }}
              defaultZoom={DEFAULT_ZOOM}
              disableDefaultUI
              zoomControl
              gestureHandling='cooperative'
              onClick={() => setOpenId(null)}
              className={mapClass}
            >
              <FitBounds clinics={clinics} />

              {clinics.map(clinic => (
                <AdvancedMarker
                  key={clinic.id}
                  title={clinic.name}
                  position={{ lat: clinic.latitude, lng: clinic.longitude }}
                  onClick={() => setOpenId(clinic.id)}
                >
                  <ShPinIcon className='text-sh-dark-gray h-9.5 w-6.75' />
                </AdvancedMarker>
              ))}
            </Map>
          </APIProvider>
        </ErrorBoundary>
      ) : (
        <div className={mapClass} />
      )}

      {open && (
        <div className='pointer-events-none absolute inset-0 flex items-center justify-center p-4'>
          <LocationMapCard
            clinic={open}
            onClose={() => setOpenId(null)}
            className='pointer-events-auto'
          />
        </div>
      )}
    </section>
  );
};
